import { FC } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
} from '@chakra-ui/react';
import { ApiKeyTemplate } from 'types/apiKeyTemplate';
import { useRegistrationStore } from '../../../hooks';
import {
  CrawlerIdInput,
  CrawlerNameInput,
  DescriptionTextarea,
  ImageNameInput,
  VersionInput,
  AdhocSupportedIntervalCheckboxGroup,
  ConfigTemplateTextarea,
  ReleasedChannelCheckboxGroup,
  ReleasedAtDatepicker,
  ApiKeyTemplateSelect,
} from '../../Forms';

type Props = ReturnType<typeof useRegistrationStore>['form'] & {
  handleConfirm: () => void;
  apiKeyTemplates: ApiKeyTemplate[];
  isCheckUnnecessaryApiKey: boolean;
  setIsCheckUnnecessaryApiKey: () => void;
};

export const CrawlerRegistrationInputModalView: FC<Props> = ({
  control,
  register,
  formState,
  handleConfirm,
  apiKeyTemplates,
  isCheckUnnecessaryApiKey,
  setIsCheckUnnecessaryApiKey,
}) => (
  <ModalContent maxHeight="90vh">
    <ModalHeader p="40px">クローラーの登録</ModalHeader>
    <ModalCloseButton />
    <ModalBody overflowY="auto" px="40px">
      <Stack spacing="6" direction="column">
        <FormControl isRequired isInvalid={!!formState.errors.id}>
          <FormLabel>クローラーID</FormLabel>
          <CrawlerIdInput name="id" register={register} w="240px" />
          <FormErrorMessage>{formState.errors.id?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.name}>
          <FormLabel>クローラー名</FormLabel>
          <CrawlerNameInput name="name" register={register} />
          <FormErrorMessage>{formState.errors.name?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.imageName}>
          <FormLabel>イメージ名</FormLabel>
          <ImageNameInput name="imageName" register={register} />
          <FormErrorMessage>
            {formState.errors.imageName?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!formState.errors.apiKeyTemplateId}>
          <FormLabel>APIキーテンプレート</FormLabel>
          <HStack>
            <Box width="90%" display="inline-block">
              {isCheckUnnecessaryApiKey ? (
                <ApiKeyTemplateSelect
                  name="apiKeyTemplateId"
                  register={register}
                  isCheckUnnecessaryApiKey
                />
              ) : (
                <ApiKeyTemplateSelect
                  name="apiKeyTemplateId"
                  register={register}
                  placeholder="APIキーテンプレートを選択してください"
                >
                  {apiKeyTemplates.map(({ id, name }) => (
                    <option value={id} key={id}>
                      {name}
                    </option>
                  ))}
                </ApiKeyTemplateSelect>
              )}
            </Box>
            <Box width="10%" display="inline-block">
              <Checkbox
                isChecked={isCheckUnnecessaryApiKey}
                onChange={setIsCheckUnnecessaryApiKey}
              >
                不要
              </Checkbox>
            </Box>
          </HStack>
          <FormErrorMessage>
            {formState.errors.apiKeyTemplateId?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.version}>
          <FormLabel>バージョン</FormLabel>
          <VersionInput name="version" register={register} w="240px" />
          <FormErrorMessage>
            {formState.errors.version?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.description}>
          <FormLabel>説明</FormLabel>
          <DescriptionTextarea
            name="description"
            register={register}
            rows={4}
          />
          <FormErrorMessage>
            {formState.errors.description?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.releasedAt}>
          <FormLabel>リリース日</FormLabel>
          <ReleasedAtDatepicker
            name="releasedAt"
            control={control}
            inputProps={{ width: '240px' }}
          />
          <FormErrorMessage>
            {formState.errors.releasedAt?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl>
          <FormLabel>手動実行対応</FormLabel>
          <AdhocSupportedIntervalCheckboxGroup
            name="adhocSupportedIntervals"
            control={control}
          />
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.configTemplate}>
          <FormLabel>UI表示テンプレート</FormLabel>
          <ConfigTemplateTextarea
            name="configTemplate"
            register={register}
            rows={8}
          />
          <FormErrorMessage>
            {formState.errors.configTemplate?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.releasedChannel}>
          <FormLabel>リリースチャンネル</FormLabel>
          <ReleasedChannelCheckboxGroup
            name="releasedChannel"
            control={control}
            isInvalid={!!formState.errors.releasedChannel}
          />
          <FormErrorMessage>
            {
              // eslint-disable-next-line
              (formState.errors.releasedChannel as any)?.message
            }
          </FormErrorMessage>
        </FormControl>
      </Stack>
    </ModalBody>
    <ModalFooter justifyContent="center" p="40px">
      <Button
        w="140px"
        colorScheme="blackAlpha"
        onClick={handleConfirm}
        isDisabled={!formState.isValid}
      >
        確認
      </Button>
    </ModalFooter>
  </ModalContent>
);
